import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import styles from './styles.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';

export type InputNumberMode         = 'decimal' | 'currency';
export type InputNumberButtonLayout = 'stacked' | 'horizontal' | 'vertical';
export type InputNumberVariant      = 'outlined' | 'filled';
export type InputNumberSize         = 'small' | 'medium' | 'large';
export type InputNumberFloatLabel   = 'over' | 'in' | 'on';

/**
 * uwc-inputnumber — formatted numeric input with optional spinner buttons.
 *
 * @attr {number}  value               - Current value (JS property only, not reflected).
 * @attr {number}  min                 - Minimum allowed value.
 * @attr {number}  max                 - Maximum allowed value.
 * @attr {number}  step                - Increment/decrement step. Default: 1.
 * @attr {string}  locale              - BCP 47 locale tag used for display formatting. Default: en-US.
 * @attr {string}  mode                - decimal | currency. Default: decimal.
 * @attr {string}  currency            - ISO 4217 currency code (e.g. USD). Required when mode=currency.
 * @attr {string}  currency-display    - symbol | code | name | narrowSymbol. Default: symbol.
 * @attr {boolean} use-grouping        - Enable digit grouping (thousands separator). Default: true.
 * @attr {number}  min-fraction-digits - Minimum fraction digits shown.
 * @attr {number}  max-fraction-digits - Maximum fraction digits shown/stored.
 * @attr {string}  prefix              - Static text prepended to the formatted display value.
 * @attr {string}  suffix              - Static text appended to the formatted display value.
 * @attr {boolean} show-buttons        - Show increment/decrement spinner buttons. Default: false.
 * @attr {string}  button-layout       - stacked | horizontal | vertical. Default: stacked.
 * @attr {string}  increment-button-icon - Bootstrap icon name for the increment button. Default: chevron-up.
 * @attr {string}  decrement-button-icon - Bootstrap icon name for the decrement button. Default: chevron-down.
 * @attr {string}  variant             - outlined | filled. Default: outlined.
 * @attr {string}  size                - small | medium | large. Default: medium.
 * @attr {string}  label               - Floating label text. Activates float-label mode.
 * @attr {string}  float-label         - over | in | on. Default: over.
 * @attr {boolean} disabled            - Disable the control.
 * @attr {boolean} readonly            - Make the control read-only.
 * @attr {boolean} invalid             - Mark as invalid (red border).
 * @attr {boolean} fluid               - Stretch to fill container width.
 * @attr {string}  name                - Input name for form submission.
 * @attr {string}  placeholder         - Placeholder text shown when empty.
 * @attr {string}  input-id            - id attribute for the inner <input> element.
 *
 * @fires uwc-value-change - Fires when value is committed. detail: { value: number|null, formattedValue: string }
 * @fires uwc-input        - Fires on every keystroke. detail: { value: string }
 * @fires uwc-focus        - Fires on focus.
 * @fires uwc-blur         - Fires on blur. detail: { value: number|null }
 *
 * @csspart wrapper - The outer container element.
 *
 * @cssprop --uwc-inputnumber-bg                - Background color.
 * @cssprop --uwc-inputnumber-border            - Border shorthand.
 * @cssprop --uwc-inputnumber-radius            - Border radius.
 * @cssprop --uwc-inputnumber-color             - Text color.
 * @cssprop --uwc-inputnumber-placeholder-color - Placeholder text color.
 * @cssprop --uwc-inputnumber-font-size         - Font size.
 * @cssprop --uwc-inputnumber-padding-x         - Horizontal padding for the input.
 * @cssprop --uwc-inputnumber-padding-y         - Vertical padding for the input.
 * @cssprop --uwc-inputnumber-button-width      - Width of each spinner button. Default: 2.25rem.
 * @cssprop --uwc-inputnumber-btn-bg            - Button background color.
 * @cssprop --uwc-inputnumber-btn-color         - Button icon color.
 * @cssprop --uwc-inputnumber-btn-hover-bg      - Button hover background color.
 * @cssprop --uwc-inputnumber-btn-hover-color   - Button hover icon color.
 * @cssprop --uwc-inputnumber-label-active-color - Floating label color when active.
 */
export class UwcInputNumber extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Value ─────────────────────────────────────────────────────────

  @property({ type: Number, attribute: false }) value: number | null = null;
  @property({ type: Number }) min?: number;
  @property({ type: Number }) max?: number;
  @property({ type: Number }) step = 1;

  // ── Formatting ────────────────────────────────────────────────────

  @property() locale = 'en-US';
  @property() mode: InputNumberMode = 'decimal';
  @property() currency?: string;
  @property({ attribute: 'currency-display' })
  currencyDisplay: 'symbol' | 'code' | 'name' | 'narrowSymbol' = 'symbol';
  @property({ type: Boolean, attribute: 'use-grouping' }) useGrouping = true;
  @property({ type: Number, attribute: 'min-fraction-digits' }) minFractionDigits?: number;
  @property({ type: Number, attribute: 'max-fraction-digits' }) maxFractionDigits?: number;
  @property({ attribute: 'prefix' }) prefixText?: string;
  @property({ attribute: 'suffix' }) suffixText?: string;

  // ── Buttons ───────────────────────────────────────────────────────

  @property({ type: Boolean, attribute: 'show-buttons' }) showButtons = false;
  @property({ attribute: 'button-layout' }) buttonLayout: InputNumberButtonLayout = 'stacked';
  @property({ attribute: 'increment-button-icon' }) incrementButtonIcon = 'chevron-up';
  @property({ attribute: 'decrement-button-icon' }) decrementButtonIcon = 'chevron-down';

  // ── Appearance ────────────────────────────────────────────────────

  @property({ reflect: true }) variant: InputNumberVariant = 'outlined';
  @property({ reflect: true }) size: InputNumberSize = 'medium';
  @property() label?: string;
  @property({ attribute: 'float-label', reflect: true }) floatLabel: InputNumberFloatLabel = 'over';

  // ── State ─────────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) fluid    = false;

  // ── Form / A11y ───────────────────────────────────────────────────

  @property() name?: string;
  @property() placeholder?: string;
  @property({ attribute: 'input-id' }) inputId?: string;

  // ── Internal ──────────────────────────────────────────────────────

  @state() private _displayValue = '';
  @state() private _focused = false;
  @query('input') private _inputEl?: HTMLInputElement;

  private _formatter?: Intl.NumberFormat;
  private _numericValue: number | null = null;
  private _groupSep   = ',';
  private _decimalSep = '.';
  private _repeatTimer?: ReturnType<typeof setTimeout>;
  private _repeatInterval?: ReturnType<typeof setInterval>;

  // ── Lifecycle ─────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this._buildFormatter();
    this._numericValue = this.value;
    this._displayValue = this._format(this.value);
  }

  override willUpdate(changed: Map<string, unknown>) {
    const formatKeys = new Set([
      'locale', 'mode', 'currency', 'currencyDisplay',
      'useGrouping', 'minFractionDigits', 'maxFractionDigits',
    ]);
    if ([...formatKeys].some(k => changed.has(k))) {
      this._buildFormatter();
    }
    if (changed.has('value')) {
      this._numericValue = this.value;
      if (!this._focused) {
        this._displayValue = this._format(this.value);
      }
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._stopRepeat();
  }

  override firstUpdated(): void {
    const anchor = this.shadowRoot?.querySelector('input') ?? null;
    this._validation.setAnchor(anchor);
    const hasVal = this._numericValue !== null;
    this._validation.syncFormValue(hasVal ? String(this._numericValue) : null);
    this._validation.revalidate(hasVal ? String(this._numericValue) : '', { required: this.required });
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    super.updated(changed);
    if (changed.has('required')) {
      this._validation.revalidate(
        this._numericValue !== null ? String(this._numericValue) : '',
        { required: this.required },
      );
    }
  }

  // ── Format helpers ────────────────────────────────────────────────

  private _buildFormatter() {
    const opts: Intl.NumberFormatOptions = { useGrouping: this.useGrouping };

    if (this.mode === 'currency' && this.currency) {
      opts.style           = 'currency';
      opts.currency        = this.currency;
      opts.currencyDisplay = this.currencyDisplay;
    } else {
      opts.style = 'decimal';
    }

    if (this.minFractionDigits !== undefined) opts.minimumFractionDigits = this.minFractionDigits;
    if (this.maxFractionDigits !== undefined) opts.maximumFractionDigits = this.maxFractionDigits;

    try {
      this._formatter = new Intl.NumberFormat(this.locale, opts);
      const parts     = new Intl.NumberFormat(this.locale).formatToParts(1234567.89);
      this._groupSep   = parts.find(p => p.type === 'group')?.value   ?? '';
      this._decimalSep = parts.find(p => p.type === 'decimal')?.value ?? '.';
    } catch {
      this._formatter  = new Intl.NumberFormat('en-US', opts);
      this._groupSep   = ',';
      this._decimalSep = '.';
    }

    // Reformat current display
    if (!this._focused) {
      this._displayValue = this._format(this._numericValue);
    }
  }

  private _format(val: number | null): string {
    if (val === null || val === undefined) return '';
    if (!this._formatter) return String(val);
    let str = this._formatter.format(val);
    if (this.prefixText) str = this.prefixText + str;
    if (this.suffixText) str = str + this.suffixText;
    return str;
  }

  private _parse(str: string): number | null {
    if (!str || str.trim() === '') return null;
    let s = str.trim();
    if (this.prefixText && s.startsWith(this.prefixText)) s = s.slice(this.prefixText.length).trim();
    if (this.suffixText && s.endsWith(this.suffixText))   s = s.slice(0, -this.suffixText.length).trim();
    if (this._groupSep) s = s.split(this._groupSep).join('');
    if (this._decimalSep !== '.') s = s.replace(this._decimalSep, '.');
    s = s.replace(/[^\d.\-]/g, '');
    if (!s || s === '-') return null;
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  }

  // ── Value management ──────────────────────────────────────────────

  private _clamp(val: number): number {
    let v = val;
    if (this.max !== undefined && v > this.max) v = this.max;
    if (this.min !== undefined && v < this.min) v = this.min;
    return v;
  }

  private _commitValue(val: number | null, originalEvent?: Event) {
    if (val !== null) {
      val = this._clamp(val);
      if (this.maxFractionDigits !== undefined) {
        val = parseFloat(val.toFixed(this.maxFractionDigits));
      }
    }
    const prev = this._numericValue;
    this._numericValue = val;
    this.value         = val;
    this._displayValue = this._format(val);

    if (prev !== val) {
      this.dispatchEvent(new CustomEvent('uwc-value-change', {
        bubbles: true, composed: true,
        detail: { value: val, formattedValue: this._displayValue, originalEvent },
      }));
    }
    this._validation.syncFormValue(val !== null ? String(val) : null);
  }

  private _step(dir: 1 | -1, e: Event) {
    if (this.disabled || this.readonly) return;
    const base = this._numericValue ?? (dir === 1 ? (this.min ?? 0) : (this.max ?? 0));
    let next = base + dir * this.step;
    if (this.maxFractionDigits !== undefined) {
      next = parseFloat(next.toFixed(this.maxFractionDigits));
    }
    this._commitValue(next, e);
    // Sync native input immediately (bypasses next render cycle)
    if (this._inputEl) this._inputEl.value = this._displayValue;
  }

  // ── Event handlers ────────────────────────────────────────────────

  private _onInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this._displayValue = input.value;
    this.dispatchEvent(new CustomEvent('uwc-input', {
      bubbles: true, composed: true,
      detail: { value: input.value, originalEvent: e },
    }));
  }

  private _onFocus(e: FocusEvent) {
    this._focused = true;
    if (this._numericValue !== null) {
      this._displayValue = String(this._numericValue);
    }
    this.updateComplete.then(() => this._inputEl?.select());
    this.dispatchEvent(new CustomEvent('uwc-focus', {
      bubbles: true, composed: true,
      detail: { originalEvent: e },
    }));
  }

  private _onBlur(e: FocusEvent) {
    this._focused = false;
    this._commitValue(this._parse(this._displayValue));
    if (this._inputEl) this._inputEl.value = this._displayValue;
    this._validation.handleChange(
      this._numericValue !== null ? String(this._numericValue) : '',
      { required: this.required },
    );
    this._validation.setTouched(true);
    this.dispatchEvent(new CustomEvent('uwc-blur', {
      bubbles: true, composed: true,
      detail: { value: this._numericValue, originalEvent: e },
    }));
  }

  private _onKeyDown(e: KeyboardEvent) {
    if (this.readonly || this.disabled) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._step(1, e);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._step(-1, e);
    }
  }

  // ── Long-press repeat ─────────────────────────────────────────────

  private _startRepeat(dir: 1 | -1, e: Event) {
    e.preventDefault(); // keep focus on input
    this._step(dir, e);
    this._repeatTimer = setTimeout(() => {
      this._repeatInterval = setInterval(() => this._step(dir, e), 40);
    }, 500);
  }

  private _stopRepeat() {
    clearTimeout(this._repeatTimer);
    clearInterval(this._repeatInterval);
  }

  // ── Render helpers ────────────────────────────────────────────────

  private _renderButton(dir: 1 | -1) {
    const isInc = dir === 1;
    const icon  = isInc ? this.incrementButtonIcon : this.decrementButtonIcon;
    const label = isInc ? 'Increment' : 'Decrement';
    const cls   = isInc ? 'uwc-in__btn--inc' : 'uwc-in__btn--dec';
    return html`
      <button
        type="button"
        class="uwc-in__btn ${cls}"
        aria-label=${label}
        tabindex="-1"
        ?disabled=${this.disabled || this.readonly}
        @mousedown=${(e: MouseEvent) => this._startRepeat(dir, e)}
        @mouseup=${() => this._stopRepeat()}
        @mouseleave=${() => this._stopRepeat()}
        @touchstart=${(e: TouchEvent) => { e.preventDefault(); this._startRepeat(dir, e); }}
        @touchend=${() => this._stopRepeat()}
        @touchcancel=${() => this._stopRepeat()}
      >
        <uwc-icon name=${icon} size=${this.size === 'small' ? '11px' : this.size === 'large' ? '18px' : '14px'} aria-hidden="true"></uwc-icon>
      </button>
    `;
  }

  private _renderField() {
    const floatMode  = !!this.label;
    const placeholder = floatMode ? (this.placeholder ?? ' ') : this.placeholder;
    return html`
      <div class="uwc-in__field">
        <input
          id=${ifDefined(this.inputId)}
          name=${ifDefined(this.name)}
          type="text"
          inputmode="decimal"
          role="spinbutton"
          autocomplete="off"
          .value=${live(this._displayValue)}
          placeholder=${ifDefined(placeholder)}
          aria-label=${ifDefined(!this.inputId ? (this.label ?? undefined) : undefined)}
          aria-valuenow=${ifDefined(this._numericValue !== null ? this._numericValue : undefined)}
          aria-valuemin=${ifDefined(this.min)}
          aria-valuemax=${ifDefined(this.max)}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          @input=${this._onInput}
          @focus=${this._onFocus}
          @blur=${this._onBlur}
          @keydown=${this._onKeyDown}
        />
        ${floatMode ? html`
          <label class="uwc-in__label" for=${ifDefined(this.inputId)}>
            ${this.label}
          </label>` : nothing}
      </div>
    `;
  }

  override render() {
    const withBtns  = this.showButtons;
    const layout    = this.buttonLayout;
    const floatMode = !!this.label;

    const classes = classMap({
      'uwc-in':                              true,
      [`uwc-in--${this.variant}`]:           true,
      'uwc-in--small':                       this.size === 'small',
      'uwc-in--large':                       this.size === 'large',
      'uwc-in--invalid':                     this.invalid,
      'uwc-in--disabled':                    this.disabled,
      'uwc-in--focused':                     this._focused,
      'uwc-in--buttons':                     withBtns,
      'uwc-in--stacked':                     withBtns && layout === 'stacked',
      'uwc-in--horizontal':                  withBtns && layout === 'horizontal',
      'uwc-in--vertical':                    withBtns && layout === 'vertical',
      'uwc-in--float':                       floatMode,
      [`uwc-in--float-${this.floatLabel}`]:  floatMode,
    });

    if (!withBtns) {
      return html`<div part="wrapper" class=${classes}>${this._renderField()}</div>`;
    }

    if (layout === 'horizontal') {
      return html`
        <div part="wrapper" class=${classes}>
          ${this._renderButton(-1)}
          ${this._renderField()}
          ${this._renderButton(1)}
        </div>
      `;
    }

    if (layout === 'vertical') {
      return html`
        <div part="wrapper" class=${classes}>
          ${this._renderButton(1)}
          ${this._renderField()}
          ${this._renderButton(-1)}
        </div>
      `;
    }

    // stacked (default)
    return html`
      <div part="wrapper" class=${classes}>
        ${this._renderField()}
        <div class="uwc-in__stacked-btns">
          ${this._renderButton(1)}
          ${this._renderButton(-1)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-inputnumber': UwcInputNumber; }
}
